import { sortBy } from 'lodash';
import { fromUrl, parseDomain } from 'parse-domain';
import { useQuery } from '@tanstack/react-query';
import { GlobeIcon } from '@phosphor-icons/react';
import _ from 'lodash';
import { getAllEventCounts } from './api/widgets-service';
import { AggregationWidget } from './aggregation-widget';
import { useWidgetsContext } from './widgets-context';
import type { EventData, WidgetProps } from './types';

const faviconUrl = (domain: string) => `https://favicone.com/${domain}?s=32`;

// Favicone 404s for domains it doesn't have, and an <img> load failure is logged
// to the console regardless of onError. A CSS background-image that 404s is
// silent, so render the globe as the base layer and paint the favicon over it:
// a real favicon covers the globe, a missing one leaves the globe showing, with
// no console noise. (Verified: an <img> 404 logs, a background-image 404 does not.)
function Favicon({ domain }: { domain: string }) {
  return (
    <span className="relative inline-flex size-4 shrink-0 items-center justify-center">
      <GlobeIcon className="size-4 text-muted-foreground/50" />
      <span
        aria-hidden
        className="absolute inset-0 bg-contain bg-center bg-no-repeat"
        style={{ backgroundImage: `url("${faviconUrl(domain)}")` }}
      />
    </span>
  );
}

const PAGEVIEW = 'Pageview';

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

function getDomain(url: string) {
  const parseResult = parseDomain(fromUrl(url));
  if (parseResult.type === 'LISTED') {
    const { domain, topLevelDomains } = parseResult;
    return `${domain}.${topLevelDomains.join('.')}`;
  }

  return false;
}

function getDisplayedUrl(url: string) {
  return url
    .replace(/(^\w+:|^)\/\//, '')
    .replaceAll('www.', '')
    .replace(/\/+$/, '');
}

function filterData(data: EventData[], totalPageViews: number) {
  const sum = _.reduce(data, (a, n) => a + parseInt(n.count.toString()), 0);
  const directVisits = totalPageViews - sum;
  const filtered = _.filter(
    [{ property: '(None)', count: directVisits } as EventData, ...(data ?? [])],
    (d) =>
      (d.property && isValidHttpUrl(d.property)) ||
      (d.property === '(None)' && Number(d.count) > 0),
  );

  return sortBy(filtered, (d) => -d.count);
}

function renderProperty(url: string) {
  if (url === '(None)') {
    return (
      <div className="relative z-10 flex items-center gap-2 pl-2 font-semibold">
        Direct / None
      </div>
    );
  }
  const domainUrl = getDomain(url);

  return (
    <div className="relative z-10 flex items-center gap-2 pl-2 font-semibold">
      {domainUrl && <Favicon domain={domainUrl} />}
      <a
        className="cursor-pointer truncate hover:underline"
        href={url}
        rel="noreferrer"
        target="_blank"
      >
        {getDisplayedUrl(url)}
      </a>
    </div>
  );
}

export const ReferrerWidget = ({
  internal,
  dateRange,
  onDelete,
  params,
}: WidgetProps) => {
  const { api, projectId, filterGroups } = useWidgetsContext();
  const { data: eventsData } = useQuery({
    queryKey: ['events-all:count', projectId, dateRange, filterGroups],
    queryFn: () =>
      getAllEventCounts(api, {
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate.getTime(),
        endDate: dateRange.endDate.getTime(),
        filterGroups,
      }),
  });
  const title = params.title;

  const totalPageViews =
    eventsData?.[PAGEVIEW]?.reduce(
      (a: number, n: EventData) => a + parseInt(n.count.toString()),
      0,
    ) ?? 0;

  return (
    <AggregationWidget
      dateRange={dateRange}
      filterData={(data) => filterData(data, totalPageViews)}
      internal={internal}
      params={{
        eventName: PAGEVIEW,
        property: '_referrer',
        title,
      }}
      renderProperty={renderProperty}
      onDelete={onDelete}
    />
  );
};
